import { Injectable, NgZone } from '@angular/core';

import { take, map, auditTime, tap, filter } from 'rxjs/operators';
import { Observable, fromEvent, EMPTY } from 'rxjs';
import { ReplaySubject } from 'rxjs';
import { Subject } from 'rxjs';

import mapboxgl, { AnyLayer, MapLayerEventType, PaddingOptions } from 'mapbox-gl';
import { MapMouseEvent, BackgroundLayer } from 'mapbox-gl';
import { Map, LngLatBoundsLike, LngLatLike, GeoJSONSource, MapboxOptions, LngLat, Point, FitBoundsOptions } from 'mapbox-gl';
import { Feature, FeatureCollection, LineString, Position } from 'geojson';

import { MoteService } from './mote.service';
import { DeviceService } from './device.service';
import { IconService } from './icon.service';

import { JourneyPointFeature, JourneyRoute, JourneyPoint } from '../components/transportation/transportation.model';
import { MapStyle, Coords, Mutex, Mote, MoteType, Geometry, IconSet, MapUtils, MotesPaths, Media, MediaType } from '../models';
import { layers, circleRadius } from './map-constants';
import { ProjectService } from './project.service';
import { StyleService } from './style.service';
import { AutoSub, AutoSubOnDestroy } from '../providers/auto-sub';
import { bbox, lineString, point } from '@turf/turf';

export class MapCenter {
    constructor(public coords: Coords = null,
        public zoom: number = null,
        public bearing: number = null,
        public pitch: number = null,
        public pxFromTop: number = null,
        public pxFromLeft: number = null) { }
}

export class MapClickEvent {
    public featureClicked: boolean = false;
    constructor(
        public features: Feature[],
        public marker: HTMLElement,
        public position: Coords,
        public pressTime: number,
        public mapMoved: boolean,
        public originEvent: Event
    ) {
        if(features.length) this.featureClicked = true;
    }
}

export interface MapZoomState {
    center: [number, number],
    zoom: number,
    bearing: number,
    pitch: number,
    duration: number,
    delayEndEvents?: number,
    offset?: [number, number]
}

export class MapOptions implements MapboxOptions {
    public style: string;
    public container: string = 'map';
    public center: LngLatLike;
    public zoom: number = 13;
    public transition = {
        duration: 2000
    };

    public keyboard = false;
    public accessToken;
    public layers = layers;
    public preserveDrawingBuffer = false;
    public loadIconSpriteSheet: boolean = true;

    constructor(accessToken: string = MotesPaths.MAPBOX_ACCESS_TOKEN,
        // TODO: Load real style of selected world and separate accessToken and style string - reimplement this class or delete it
        // styleString:string = "mapbox://styles/mapbox/dark-v9",
        // styleString: string = "mapbox://styles/mapbox/dark-v10",
        // styleString: string = "mapbox://styles/snazami/cjeo8r9zs4g952rozaiskt0r0",
        centerCoords: Coords = new Coords(55.693182, 12.550525)) {
        this.accessToken = accessToken;
        this.center = centerCoords.longLat();
        this.style = MotesPaths.MAPBOX_DEFAULT_STYLE;
    }
}

@Injectable({ providedIn: 'root' })
@AutoSubOnDestroy()
export class MapService {
    public map: Map = null;
    public previewModeEnabled: boolean = true;

    // The active map style
    public activeMapStyle: MapStyle;
    // The active project's default map style
    public projectMapStyle: MapStyle;

    public initialized = false;
    private initSubject: ReplaySubject<Map> = new ReplaySubject<Map>(1);
    private loadSubject: ReplaySubject<Map> = new ReplaySubject<Map>(1);

    public is3DModeEnabled = false;
    public motePlacementPromise = null;
    public moteToPlace: Mote = null;

    private mapOptions: MapOptions = new MapOptions();
    private layers: any;
    private lastClickTime: number = 0;
    private mapMoved: boolean = false;
    private flyMutex = new Mutex();

    // Make loaded motes accesible for others to use
    public motes: Mote[] = [];

    private directionsList: Feature[] = [];
    private directionsStopList: JourneyPointFeature[] = [];
    private geocodeFeatureList: JourneyPointFeature[] = [];

    // Layer consts
    private readonly DEFAULT_CLICK_LAYERS = ["moteClickLayer", "routesClick", "directionsFeature", "searchResults", "tourClickLayer", "storyClickLayer"];
    private readonly DIRECTIONS_LAYER = layers.directions.id;;
    private readonly DIRECTIONS_FEATURE = layers.directionsFeature.id;
    private readonly DIRECTIONS_DOT_LAYERS = [layers.directionsDottedBorder.id, layers.directionsDotted.id];
    private readonly DIRECTIONS_TEXT = layers.directionsText.id;
    private readonly LAYER_3D = layers.layer3D.id;
    private readonly MOTES_LAYER = layers.motes.id;
    private readonly ROUTES_LAYER = layers.routes.id;
    private readonly AVAILABLE_MOTE_LAYER = layers.availableMotesAnimation.id;

    constructor(private ngZone: NgZone, private iconService: IconService, private moteService: MoteService, private deviceService: DeviceService, private projectService: ProjectService, private styleService: StyleService) {
        // Avoid referencing window in SSR (prerender)
        if(typeof window == 'undefined') return;

        // Expose map service to Cypress e2e test 
        if((window as any)?.Cypress) {
            (window as any).MapService = this;
        }
    }

    public awaitInit() {
        return this.initSubject.pipe(take(1)).toPromise();
    }

    public awaitPreLoad() {
        return this.loadSubject.pipe(take(1)).toPromise();
    }

    public awaitTilesLoaded() {
        return new Promise<void>(async (resolve) => {
            // Await map init
            await this.awaitInit();
            // Wait for map tiles to be loaded
            if(this.map.areTilesLoaded()) resolve();
            this.map.once('sourcedata', () => resolve());
        });
    }

    public async init(mapOptions: MapOptions = this.mapOptions, mapStyle?: MapStyle): Promise<mapboxgl.Map> {
        if(this.deviceService.isPlatformServer()) {
            // Fake map
            this.map = {} as any;
            // Act as if map was working
            // this.initSubject.next();
            return new Promise(resolve => { });
        }

        // If a map style is not passed to the method, get the default map style
        const inputOrDefaultStyle = mapStyle ? mapStyle : await this.styleService.getMapStyle('default');

        // Run all map mouse event listners and timers out side of Angular 
        // - this improves performance significantly as angular is relieved from running change detection after each event.
        // This decreases CPU load from between 80% - 90% peak on (my though shitty) desktop when moving the mouse to 18 - 22%
        // and the pinch goes from lagging a lot (with the debug console open) to pretty smooth (even compiled with debug)
        // ! Map must run all events coming from inside with the intent of manipulate outside components inside of Angular zone (ie. ngZone.run())
        const map = await this.ngZone.runOutsideAngular(() => this.initOutsideAngular(mapOptions, inputOrDefaultStyle));

        // Subscribe to map moved events to detect  watchMapClick and watchMapRelease last click time and map moved flag
        // As this is a potential heavy subscription we only do this one time, though watchClick and watchMapRelease could have
        // multiple subscriptions. Though this is not a really clean 'RxJs' pipe way of doing things (updating member variables like this),
        // it seems to be the best performing solution (unless we completely rethink and refactor our map event handlers).
        AutoSub(this).reg['mapMovedEvents'] = fromEvent(this.map, 'move').pipe(tap((ev: mapboxgl.MapMouseEvent) => {
            this.lastClickTime = 0;
            this.mapMoved = true;
        })).subscribe();

        return map;
    }

    public initOutsideAngular(mapOptions: MapOptions, mapStyle: MapStyle): Promise<mapboxgl.Map> {
        if(this.initialized) {
            return this.initSubject.pipe(take(1)).toPromise();
        }

        this.initialized = true;

        // Save map options for re init
        this.mapOptions = mapOptions;
        this.layers = mapOptions.layers;

        // Create new map instance using mapAccessToken and mapOptions
        mapboxgl.accessToken = mapOptions.accessToken;

        // Save map style to be used for other services
        this.activeMapStyle = mapStyle;
        this.projectMapStyle = mapStyle;
        // Get the Mapbox map style url and set it on map options
        if(mapStyle.style) mapOptions.style = mapStyle.style;

        this.map = new Map(mapOptions);
        // When map has loaded --> Add intial sources and layers and resolve
        this.map.on('load', async () => {
            // Indicate that first part of map load finished (tiles rendered)
            this.loadSubject.next(this.map);
            // Load icons before init of layers
            if(mapOptions.loadIconSpriteSheet) {
                await this.loadIcons().catch(() => { console.log("Could not load map icon sprite sheet") });
            }

            this.addSources();
            this.addLayers();
            // Poll map loaded
            let timer = setInterval(() => {
                if(!this.map.loaded()) {
                    return;
                }
                this.initMoteMouseEvents();
                // Map loaded - resolve promise
                this.initialized = true;
                clearInterval(timer);
                this.initSubject.next(this.map);
                console.log("* Map initialize done");
            }, 200);
        });
        this.zoomOnClusterClick();
        return this.initSubject.pipe(take(1)).toPromise();
    }

    private zoomOnClusterClick() {
        this.watchMapClick(['clusters']).subscribe((ev) => {
            // Validate event
            if(!ev || !ev.featureClicked) return;
            let trigger = ev.features[0] as any;
            if(!trigger) return;
            else if(trigger.properties.cluster) {
                let clickCoords = new Coords(trigger.geometry.coordinates[1], trigger.geometry.coordinates[0]);
                this.flyTo(clickCoords, (this.map.getZoom() + 2));
            }
        })
    }

    private initMoteMouseEvents() {
        // Change the cursor style on mote hover
        this.map.on('mouseenter', 'motes', ev => this.map.getCanvas().style.cursor = 'pointer');
        this.map.on('mouseleave', 'motes', ev => this.map.getCanvas().style.cursor = '');
    }

    public placeMote(mote: Mote): Promise<boolean> {
        if(this.motePlacementPromise) {
            // Resolve previous unresolved promise if any
            this.motePlacementPromise.next(false);
            this.motePlacementPromise.complete();
        }
        // Prepare to place mote in map
        this.moteToPlace = mote;
        this.flyTo(mote.position, 15);
        // Asynch promis resolved on placement finish
        this.motePlacementPromise = new Subject<boolean>();
        return this.motePlacementPromise.toPromise();
    }

    public placeMoteDone(success: boolean): void {
        // Report result to create listener
        this.motePlacementPromise.next(success);
        this.motePlacementPromise.complete();
        // Reset state
        this.motePlacementPromise = null;
        //this.moteToPlace = null;
    }

    public resizeMap(): void {
        // If map has been initialized --> resize to make it fits container (if it has been resized e.g.)
        if(this.initialized) return;
        if(!this.map) return;
        this.map.resize();
    }

    public setCenter(coords: Coords): void {
        // Do nothing in SSR Mode
        if(this.deviceService.isPlatformServer()) return;
        // Check coords validity
        if(!coords || !coords.isValid) return console.warn("Invalid coords given", coords);
        // Check if map exists
        if(!this.map) return;
        // Set the center of the map
        this.map.setCenter(coords.longLat());
    }

    public getCenter(): Coords {
        let center = this.map.getCenter();
        return new Coords(center.lat, center.lng, true);
    }

    /**
    * Fly to position using Coords, optional params: zoom and speed
    */
    public flyTo(position: Coords, zoom: number = null, speed: number = 1, maxDuration: number = 2000): Promise<Coords> {
        if(!zoom) zoom = this.map.getZoom();

        // Dont fly any where if position is null
        if(!position || !position.isValid) {
            console.error("Could not fly to because position is " + (position ? "invalide" : "null"));
            return;
        }

        // Fly to position using position and zoom
        this.map.flyTo({
            center: <LngLatLike>position.longLat(),
            zoom: zoom,
            speed: speed,
            maxDuration: maxDuration
        });

        return this.awaitMoveEnd();
    }

    private awaitMoveEnd(): Promise<Coords> {
        return new Promise(async (resolve) => {
            // Wait small amout of time for any movement to get registered in map
            await new Promise<void>(timeout => setTimeout(() => timeout(), 20));

            // Resolve now if map already stopped moving and zooming
            let isMoving = (this.map.isEasing() || this.map.isMoving());
            if(!isMoving) return resolve(this.getCenter());

            // Delay resolve until movement has stopped
            this.map.once("moveend", end => {
                resolve(this.getCenter());
            });
        });
    }

    /**
    * Fit map zoom to an array of motes
    */
    public fitMapBoundsToMotes(motes: Mote[], speed: number = 0.8, zoomPadding: mapboxgl.PaddingOptions = null, maxZoom: number = 16, duration: number = 10000, preventBearingReset: boolean = false): Promise<Coords> {
        if(!motes || motes.length <= 0) {
            console.error("No motes to fit map bounds to? ", motes);
            return Promise.resolve(new Coords(0, 0, false));
        }

        // Calculate bounds
        let moteBounds = this.calcBoundingBox(motes);
        if(!moteBounds) {
            console.warn("Could not fit bounds to motes as non of the motes have a valid position");
            return Promise.resolve(new Coords(0, 0, false));
        }

        let padding = zoomPadding ? zoomPadding : { top: 80, bottom: 80, left: 80, right: 80 };

        // Prevent map bearing change on fit to bounding
        // This requies us to manually calculate the rotated bounds rect to workaround mapbox bug, 
        // For now just accept reset of bearing when zooming to motes
        // OBS: The implementation of preventBearingReset does not work for multiple motes, as this would probably reuire rotation of every single point and 
        // then calculate the min max bounding box. For now it seems like a fine compromise to reset rotation when a collection/route is selected. So day MapBox
        // might fix their code, https://github.com/mapbox/mapbox-gl-js/issues/2112 and https://github.com/mapbox/mapbox-gl-js/issues/1338
        let mapPadding = preventBearingReset ? { top: 0, bottom: 0, left: 0, right: 0 } : padding;
        let mapBearing = preventBearingReset ? this.map.getBearing() : null;
        let mapBounds = preventBearingReset ? this.rotateBoundingBox(moteBounds, mapBearing, padding) : moteBounds;

        let options: FitBoundsOptions = { padding: mapPadding, maxZoom: maxZoom, maxDuration: duration, bearing: mapBearing }

        if(speed > 0) options.speed = speed;
        // Use duration time for fit bounds animation
        if(speed <= 0) options.duration = duration;

        // Fit map bounds of motes
        this.map.fitBounds(mapBounds, options);
        // Use speed argument to fit bounds on map
        return this.awaitMoveEnd();
    }

    /**
    * Fit map zoom to a single mote with children
    */
    private calcBoundingBox(motes: Mote[]): [LngLat, LngLat] {
        if(!motes || motes.length <= 0) {
            console.warn("Can not calc bounds of empty mote array");
            return null;
        }
        // Convert positions to longLat array
        const coordinates = motes.filter(mote => mote.position).map(mote => mote.position.longLat());
        if(coordinates.length <= 0) return null;

        // Find bounding box
        const box = (coordinates.length > 1) ? bbox(lineString(coordinates)) : bbox(point(coordinates[0]));
        // Convert to mapbox lat long like bounds
        return [LngLat.convert({ lng: box[0], lat: box[1] }), LngLat.convert({ lng: box[2], lat: box[3] })];
    }

    rotateBoundingBox(bounds: [LngLat, LngLat], bearing: number, padding: mapboxgl.PaddingOptions): LngLatBoundsLike {
        // Convert bounds to pixels
        let leftTop = this.map.project(bounds[0]);
        let rightBottom = this.map.project(bounds[1]);

        // Find center of bounding box
        let centerX = leftTop.x + ((rightBottom.x - leftTop.x) / 2);
        let centerY = rightBottom.y + ((rightBottom.y - leftTop.y) / 2);
        let center = new Point(centerX, centerY);

        // Calculate rotated bounding box and unproject to latlng
        let rotateLeftTop = this.rotatePoint(leftTop, bearing, center);
        let rotateRightBottom = this.rotatePoint(rightBottom, bearing, center);

        rotateLeftTop.x -= padding.left;
        rotateLeftTop.y -= padding.top;
        rotateRightBottom.x += padding.right;
        rotateRightBottom.y += padding.bottom;

        // Return rotated bounds
        return [this.map.unproject(rotateLeftTop), this.map.unproject(rotateRightBottom)];
    }

    rotatePoint(p: Point, bearing: number, center: Point): Point {
        const theta = bearing * (Math.PI / 180);
        // Rotate x and y
        let x = center.x + (p.x - center.x) * Math.cos(theta) + (p.y - center.y) * Math.sin(theta);
        let y = center.y + (p.y - center.y) * Math.sin(theta) + (p.y - center.y) * Math.cos(theta);
        return new Point(x, y);
    }

    /**
    * Calculate the center/average of multiple GeoLocation coordinates (Coords[])
    */
    public calcAverageCoords(coords: Coords[]): Coords { // http://stackoverflow.com/a/14231286/538646
        if(coords.length === 1) {
            return coords[0];
        }

        // Temporary values for averages
        let x = 0.0;
        let y = 0.0;
        let z = 0.0;

        for(let coord of coords) {
            let latitude = coord.lat * Math.PI / 180;
            let longitude = coord.long * Math.PI / 180;

            // Do the magic
            x += Math.cos(latitude) * Math.cos(longitude);
            y += Math.cos(latitude) * Math.sin(longitude);
            z += Math.sin(latitude);
        }

        // Average all values
        let total = coords.length;
        x = x / total;
        y = y / total;
        z = z / total;

        // More magic
        let centralLongitude = Math.atan2(y, x);
        let centralSquareRoot = Math.sqrt(x * x + y * y);
        let centralLatitude = Math.atan2(z, centralSquareRoot);

        return new Coords(
            centralLatitude * 180 / Math.PI,
            centralLongitude * 180 / Math.PI,
            true
        )
    }

    public async flyToUserMoteDirections(motePosition: Coords, userPosition: Coords, zoomPadding: PaddingOptions =  { top: 120, left: 150, right: 150, bottom: 60 }) {
        // Fly to mote and user position (if not lost)
        let positions = [motePosition.longLat()];
        if(userPosition && userPosition.isValid) positions.push(userPosition.longLat());
        this.fitMapBoundsToCoords(positions, zoomPadding, 1.5, true);
    }

    public async flyToMote(mote: Mote, zoom: number = null, speed: number = 1, maxDuration: number = 20000, padding: PaddingOptions = null): Promise<Coords> {
        //this.highlightMote(mote.key);
        if(!zoom) zoom = this.map.getZoom();

        // Fly to route or collection
        if(mote.type == MoteType.ROUTE || mote.type == MoteType.COLLECTION) {
            let children = await this.moteService.getChildren(mote);
            return this.fitMapBoundsToMotes(children, speed, padding, maxDuration);
        }
        else {
            return this.flyTo(mote.position, zoom, speed, maxDuration);
        }
    }

    public flyToMoteWhenReady(mote: Mote, zoom: number = 15, speed: number = 1, maxDuration: number = 20000): Promise<any> {
        return new Promise<void>(async resolve => {
            // Obtain fly lock
            await this.flyMutex.lock();
            await this.flyToMote(mote, zoom, speed, maxDuration);
            // Release fly lock
            this.flyMutex.unlock();
            resolve();
        });
    }

    public easeToWhenReady(easeOptions: mapboxgl.EaseToOptions): Promise<any> {
        return new Promise<void>(async resolve => {
            // Obtain fly lock
            await this.flyMutex.lock();
            await this.easeTo(easeOptions);
            // Release fly lock
            this.flyMutex.unlock();
            resolve();
        });
    }

    public watchDblClick(): Observable<Coords> {
        // Ignore click event handlers when rendering with mock map during SSR
        if(this.deviceService.isPlatformServer()) return EMPTY;

        let clickCoords = new Coords(0, 0);
        return fromEvent(this.map, 'dblclick').pipe(map((ev: MapMouseEvent) => {
            // Show where user has clicked
            this.ngZone.run(() => {
                clickCoords.setPosition(ev.lngLat.lat, ev.lngLat.lng);
            });
            return clickCoords;
        }));
    }

    public watchFeatureDrag(layers: string[] = this.DEFAULT_CLICK_LAYERS): Observable<MapClickEvent> {
        // Ignore move event handlers when rendering with mock map during SSR
        if(this.deviceService.isPlatformServer()) return EMPTY;

        let lastPressTime = 0;
        return fromEvent(this.map, 'mousemove').pipe(map((ev: mapboxgl.MapMouseEvent) => {
            return this.genClickEvent(ev, layers);
        }), filter((clickEvent) => {
            // When pressTime changes from 0 (mouse is up) to greater than 0 (mouse is down and moving), the dragging has begun
            const dragStarted = lastPressTime == 0 && clickEvent.pressTime > 0;
            lastPressTime = clickEvent.pressTime;
            return dragStarted;
        } ));
    }

    /**
    * Simply watch clicks on map (maybe we should obsolete watchClick?)
    */
    public watchMapClick(layers: string[] = this.DEFAULT_CLICK_LAYERS): Observable<MapClickEvent> {
        // Ignore click event handlers when rendering with mock map during SSR
        if(this.deviceService.isPlatformServer() || !this.initialized) return EMPTY;

        let clickType = this.deviceService.isTouchDevice() ? 'touchstart' : 'mousedown';

        return fromEvent(this.map, clickType).pipe(map((ev: mapboxgl.MapMouseEvent) => {
            this.lastClickTime = Date.now();
            let downEv = this.genClickEvent(ev, layers);
            // Reset map moved after sending event
            this.mapMoved = false;
            return downEv;
        }));
    }

    /**
    * Simply watch mouseup on map
    */
    public watchMapRelease(layers: string[] = this.DEFAULT_CLICK_LAYERS): Observable<MapClickEvent> {
        // Ignore click event handlers when rendering with mock map during SSR
        if(this.deviceService.isPlatformServer() || !this.initialized) return EMPTY;

        let releaseType = this.deviceService.isTouchDevice() ? 'touchend' : 'mouseup';

        return fromEvent(this.map, releaseType).pipe(map((ev: mapboxgl.MapMouseEvent) => {
            let clickEv = this.genClickEvent(ev, layers);
            this.lastClickTime = 0;
            return clickEv;
        }));
    }

    private genClickEvent(event: mapboxgl.MapMouseEvent, layers: string[] = this.DEFAULT_CLICK_LAYERS): MapClickEvent {
        if(!this.initialized) return;
        // Check if click layers have been initialized or ignore event
        let layersExist = layers.map(layer => this.map.getLayer(layer)).reduce((prevExists, nextExists) => prevExists && nextExists);
        if(!layersExist) {
            console.warn("Map click layers not initialized yet, ignore click");
            return;
        }

        // Get clicked features
        let clickedFeatures = this.map.queryRenderedFeatures(event.point, { layers: layers });
        // Get clicked marker if any
        const eventTarget = event.originalEvent.target as HTMLElement;
        const clickedMarker = (eventTarget?.className == "mapboxgl-canvas") ? null : eventTarget;
        // Get click position in GPS coordinates
        let position = event.lngLat ? new Coords(event.lngLat.lat, event.lngLat.lng) : new Coords(0, 0, false);
        // Calc press time to detect longpresses
        let pressTime = this.lastClickTime ? Date.now() - this.lastClickTime : 0;

        return new MapClickEvent(clickedFeatures, clickedMarker, position, pressTime, this.mapMoved, event.originalEvent)
    }

    /**
     * Get the features on the map at the given GPS coordinates
     */
    public getPointFeature(featureId: number): Feature {
        if(!this.initialized) return null;
        // Define id search function
        const findId = (feature) => feature.properties.id == featureId;
        // Search for id in search result list and directions stop list
        return this.geocodeFeatureList.find(findId) || this.directionsStopList.find(findId);
    }

    public animateAvailableMotes(interval: number = 2000): void {
        // Do not animate in SSR mode - that would make everything hang
        if(this.deviceService.isPlatformServer()) return;

        let showBig = false;
        setInterval(() => {
            // TODO: This animation requires around 40% of the CPU time
            // For now, just fix it by not animating when there are no available motes.
            // In the long run.. use a CSS GPU accelerated animation with its own compositing layers
            // which does not triger MapBox to repaint/re-composit
            if(this.moteService.availableMotes.length <= 0) return;

            let animateRadius = showBig ? circleRadius + 13.5 : circleRadius + 11;
            // Animate circle radius
            this.map.setPaintProperty('availableMotesAnimation', 'circle-radius', animateRadius);
            showBig = !showBig;

        }, interval);
    }

    public highlightRoute(key: string): void {
        if(!this.map.getLayer('selectedRoute')) return;
        this.map.setFilter("selectedRoute", ["==", "moteKey", key]);
    }

    /** Keep this function for backward compatibility - use drawFeatures instead*/
    public async setMotes(motes: Mote[]): Promise<void> {
        if(!motes) return console.log("No motes to load in this project?");
        // Store motes for others to use
        this.motes = motes;

        // Split mote array in routes and motes
        let routes: Array<Mote> = motes.filter(mote => (mote.type == MoteType.ROUTE || mote.properties.showQuestRoute));
        let places: Array<Mote> = motes.filter(mote => (mote.type == MoteType.PLACE && !mote.properties.isHidden));
        let events: Array<Mote> = motes.filter(mote => (mote.type == MoteType.EVENT && !mote.properties.isHidden));

        // Combine places and events
        let moteList = places.concat(events);

        await this.awaitMotesMapSources();

        // Add motes to map
        this.getSource('routes').setData(MapUtils.genFeatureCollection(routes, true));
        this.getSource('motes').setData(MapUtils.genFeatureCollection(moteList, false));
    }

    public async setStories(motes: Mote[]): Promise<void> {
        if(!motes) return console.log("No tours to load in this project?");
        // Store tours for others to use
        this.motes = motes;
        // Ensure that the `stories` source have been loaded before accessing their methods
        await this.awaitMapSource('stories');
        // Add motes to map
        this.getSource('stories').setData(MapUtils.genFeatureCollection(motes, false, true));
        // Show motes on map
    }

    public async setTours(tours: Mote[]): Promise<void> {
        if(!tours) return console.log("No tours to load in this project?");
        // Store tours for others to use
        this.motes = tours;
        // Ensure that the `tours` source have been loaded before accessing their methods
        await this.awaitMapSource('tours');
        // Add tours to map
        this.getSource('tours').setData(MapUtils.genFeatureCollection(tours, false, true));
    }

    /**
     * Check if motes map sources are available or wait for them to be initialized in map style
     */
    private async awaitMotesMapSources(): Promise<void> {
        // Check if motes and routes sources are already loaded (these sources will not be available during map style change)
        if(this.getSource('motes') && this.getSource('routes')) return;
        // Filter load events out if not both motes and routes source are available
        const loadedFilter = filter(ev => Boolean(this.getSource('motes') && this.getSource('routes')));
        // Await motes sources to be loaded
        await fromEvent(this.map, 'sourcedata').pipe(loadedFilter, take(1)).toPromise();
    }

    /**
     * Check if tour motes map sources are available or wait for them to be initialized in map style
     */
    private async awaitMapSource(source: string): Promise<void> {
        // Check source has already loaded (the source will not be available during map style change)
        if(this.getSource(source)) return;
        // Filter load events out if source is not available
        const loadedFilter = filter(ev => Boolean(this.getSource(source)));
        // Await source to be loaded
        await fromEvent(this.map, 'sourcedata').pipe(loadedFilter, take(1)).toPromise();
    }

    private getSource(source: string): GeoJSONSource {
        // Type cast source to GeoJSON Source (we now thats what we use.. year)
        return <GeoJSONSource>this.map.getSource(source);
    }

    public easeTo(cameraOptions: mapboxgl.EaseToOptions): Promise<Coords> {
        this.map.easeTo(cameraOptions);
        return this.awaitMoveEnd();
    }

    public watchMapCenter(): Observable<MapCenter> {
        let mapCenter: MapCenter = new MapCenter(new Coords(0, 0)); // Create MapCenter object for use in event subscription

        return fromEvent(this.map, 'move').pipe(auditTime(200), map(ev => {
            // Get properties from map
            let center = this.map.getCenter();
            let mapZoom = this.map.getZoom();
            let mapBearing = this.map.getBearing();
            let mapPitch = this.map.getPitch();
            let mapCenterInPixels = this.map.project([center.lng, center.lat]);

            // Assign to returnObject
            mapCenter.coords.setPosition(center.lat, center.lng, true);
            mapCenter.zoom = mapZoom;
            mapCenter.bearing = mapBearing;
            mapCenter.pitch = mapPitch;
            mapCenter.pxFromTop = mapCenterInPixels.y || null;
            mapCenter.pxFromLeft = mapCenterInPixels.x || null;
            return mapCenter;
        }));
    }

    public watchMapZoom(): Observable<number> {
        if(!this.initialized) return EMPTY;
        return fromEvent(this.map, 'zoom').pipe(auditTime(200), map(ev => {
            return this.map.getZoom();
        }));
    }

    public getMapCenter(): Coords {
        let center = this.map.getCenter();
        return new Coords(center.lat, center.lng);
    }

    public getMapZoom(): number {
        return this.map.getZoom();
    }

    /**
     * Change the style of the map
     * @param style a `MapStyle` object with a `style` property containing a Mapbox style URL: https://docs.mapbox.com/help/glossary/style-url/
     * @returns a `Promise` which resolves once the style has loaded
     */
    public async changeMapStyle(style: MapStyle): Promise<void> {
        // Prevent loading the same style repeatedly
        if(this.activeMapStyle?.key == style?.key) return;

        this.activeMapStyle = style;

        // Set style
        this.map.setStyle(style.style);

        // Add custom sources and layers anew when the style has loaded
        // This is necessary because setting a new style overrides existing custom layers: https://github.com/StoryHunt/StoryHuntStudio/issues/70
        // The `styledata` event is fired when the map's style loads or changes: https://docs.mapbox.com/mapbox-gl-js/api/map/#map.event:styledata
        await fromEvent(this.map, 'styledata').pipe(take(1)).toPromise();
        await this.loadIcons();
        this.addSources();
        this.addLayers();
    }

    public disable3DMode(zoomAnimation: MapZoomState[] = []): Promise<boolean> {
        return new Promise(async (resolve) => {
            // Return negative if 3D mode is not enabled
            if(!this.is3DModeEnabled) return resolve(false);

            this.is3DModeEnabled = false;
            // Play zoom animation 
            await this.playZoomAnimation(zoomAnimation);
            // Show the 3D building layer
            this.hideLayer(this.LAYER_3D);
            this.showLayer(this.MOTES_LAYER);
            resolve(true);
        });
    }

    public enable3DMode(zoomAnimation: MapZoomState[]): Promise<boolean> {
        return new Promise(async (resolve) => {
            // Return negative if 3D mode is already enabled
            if(this.is3DModeEnabled) return resolve(false);

            this.is3DModeEnabled = true;

            await this.awaitInit();

            // Show the 3D building layer
            this.showLayer(this.LAYER_3D);
            this.hideLayer(this.MOTES_LAYER);

            // Init start position of zoom animation (do not play it all just yet, user might not be in map view yet)
            await this.playZoomAnimation(zoomAnimation.length > 1 ? [zoomAnimation[1]] : []);

            resolve(true);
        });
    }

    public playZoomAnimation(animation: MapZoomState[]): Promise<void> {
        return new Promise<void>(async (resolve) => {
            // Hide label layers while animation (this significantly increases performance)
            this.hideLayer('road-label-large');
            this.hideLayer('road-label-medium');
            this.hideLayer('road-label-small');
            // this.hideLayer('poi-label');

            // Execute zoom animation step by step
            for(let zoomState of animation) {
                await this.easeToWhenReady(zoomState);
            }

            // Show label layers again after animation
            this.showLayer('road-label-large');
            this.showLayer('road-label-medium');
            this.showLayer('road-label-small');
            // this.showLayer('poi-label');
            resolve();
        });
    }

    /**
     * Add custom sources to the map:
     * https://docs.mapbox.com/mapbox-gl-js/style-spec/sources/
     */
    private addSources(): void {
        if(!this.map.getSource('motes'))
            this.map.addSource("motes", MapUtils.genGeoJsonSource([], true)); // no features, enable clustering

        const sources = ['click', 'routes', 'directions', 'searchResults', 'directionsFeature', 'tours', 'stories'];
        sources.forEach((source) => {
            if(!this.map.getSource(source))
                this.map.addSource(source, MapUtils.genGeoJsonSource([], false));
        });
    }

    /**
     * Add custom layers to the map:
     * https://docs.mapbox.com/mapbox-gl-js/style-spec/layers/
     */
    private addLayers(): void {
        // get the ID of the first layer of type `symbol` in order to place some layers before this
        const layers = this.map.getStyle().layers;
        const firstSymbolLayer = layers.find((layer) => layer.type === 'symbol');
        // Define layers
        const layersBeforeFirstSymbolLayer = [
            this.layers.routes,
            this.layers.routesClick,
            this.layers.selectedRoute,
            this.layers.directions,
            this.layers.directionsDottedBorder,
            this.layers.directionsDotted,
            this.layers.selectedMoteUnder,
            this.layers.directionsFeature,
            this.layers.layer3D
        ];
        const layersAfterOtherLayers = [
            this.layers.selectedMote,
            this.layers.availableMotesAnimation,
            this.layers.motes,
            this.layers.clusters,
            this.layers.clusterCount,
            this.layers.moteClickLayer,
            this.layers.storyClickLayer,
            this.layers.tourClickLayer,
            this.layers.click,
            this.layers.searchResults,
            this.layers.directionsText
        ];

        // Add layers
        layersBeforeFirstSymbolLayer.forEach((layer) => this.addMapLayer(layer, firstSymbolLayer))
        layersAfterOtherLayers.forEach((layer) => this.addMapLayer(layer));

        // Hide the 3D layer per default
        this.hideLayer(this.LAYER_3D);
    }

    private addMapLayer(layer: any, beforeLayer: AnyLayer = null) {
        // Do not add the layer if it already exist
        if(this.map.getLayer(layer.id)) return;
        // Check if source exists for the map style
        if(!this.map.getSource(layer.source)) {
            console.log(`Source of layer '${layer.id}' does not exist in style - ignoring ${layer.source} layer`);
            return;
        }
        this.map.addLayer(layer, beforeLayer?.id || null);
    }

    /**
     * Add custom icons to the map
     */
    public async loadIcons(): Promise<void> {
        // Load icon sprite list
        let [spriteList, ctx] = await this.iconService.loadSpriteSheetCanvas();
        // Extract image data
        let iconList = this.iconService.loadSpriteSheetIcons(spriteList, ctx);
        // Add icons to map
        for(let icon of iconList) {
            // Only add the image if it does not already exist
            if(!this.map.hasImage(icon.sprite.key)) {
                this.map.addImage(icon.sprite.key, icon.image);
            }
        }
    }

    public async reloadIcons(iconSet: IconSet) {
        // Re-load icon sprite
        let [spriteList, ctx] = await this.iconService.loadSpriteSheetCanvas();
        // Remove previous loaded
        for(let sprite of spriteList) {
            if(this.map.hasImage(sprite.key)) this.map.removeImage(sprite.key);
        }
        return this.loadIcons();
    }

    public async enablePreviewMode(enablePreview: boolean = this.previewModeEnabled) {
        if(!this.map) return console.error("Could not set preview mode for map:", this.map);
        // Use project map style for preview mode (disable the satellite style if enabled) 
        if(enablePreview) await this.changeMapStyle(this.projectMapStyle);
        // Toggle preview mode
        this.previewModeEnabled = enablePreview;
        this.showDrawLayers(!this.previewModeEnabled);
        this.showPreviewLayers(this.previewModeEnabled);
    }

    private showDrawLayers(show: boolean) {
        let layerz = this.map.getStyle().layers.filter(layer => (layer as BackgroundLayer).source == "mapbox-gl-draw-cold" || (layer as BackgroundLayer).source == "mapbox-gl-draw-hot");
        // Hide or show each layer
        layerz.forEach(layer => this.showLayer(layer.id, show));
    }

    private showPreviewLayers(show: boolean) {
        let showLayer = show ? 'visible' : 'none';
        this.map.setLayoutProperty("motes", 'visibility', showLayer);
        this.map.setLayoutProperty("availableMotesAnimation", 'visibility', showLayer);
        this.map.setLayoutProperty("routes", 'visibility', showLayer);
        this.map.setLayoutProperty("clusters", 'visibility', showLayer);
        this.map.setLayoutProperty("clusterCount", 'visibility', showLayer);
    }

    /**
     * Add a route to the directions layer of map
     * @param Reset Set to true in order to remove old routes from map, otherwise add route to existing route list
     */
    public addDirections(route: LineString, fitBounds: boolean = false, type: string = "WALK", padding: PaddingOptions = null, reset: boolean = false) {
        if(!route || route.coordinates.length <= 0) return;
        // Remove old directions routes from map if requested
        if(reset) this.resetDirections();

        // Create GeoJSON route feature
        let routeFeature: Feature = {
            "type": "Feature",
            "properties": { 'routeType': type },
            "geometry": route
        };

        // Add to diractions route to list 
        this.directionsList.push(routeFeature);

        // Create feature collection
        let collection: FeatureCollection = {
            "type": "FeatureCollection",
            "features": this.directionsList
        };

        // Add features to map
        this.getSource(this.DIRECTIONS_LAYER).setData(collection);
        this.showDirections();

        // Fit route to bounding box
        if(fitBounds) {
            // Fit to combined route with all directions routes
            let combined = this.combineRoutes(this.directionsList);
            this.fitMapBoundsToCoords(combined, padding);
        }
    }


    /**
    * Add a features (stops) to the directions layer of map
    * @param Reset Set to true in order to remove old routes from map, otherwise add route to existing route list
    */
    public addDirectionsStops(journey: JourneyRoute[]) {
        if(!journey) return;

        let featureList: JourneyPointFeature[] = [];

        // Create GeoJSON route feature
        journey.filter(route => route.start).forEach(route => {
            let features = route.points.map(point => point.asGeoJson());
            featureList.push(...features);
        });

        // Create feature collection
        let collection: FeatureCollection = {
            "type": "FeatureCollection",
            "features": featureList
        };

        // Add features to map
        this.getSource(this.DIRECTIONS_FEATURE).setData(collection);
        this.showDirections();
        // Save list for extranal lookup
        this.directionsStopList = featureList;
    }

    /**
     * Combine list of features from multiple routes into one
     */
    private combineRoutes(list: Feature[]): Position[] {
        return list.reduce((combined, route) => {
            if(route.geometry.type == 'LineString') combined.push(...route.geometry.coordinates);
            return combined;
        }, []);
    }

    /**
     * Remove all directions from map
     */
    public resetDirections() {
        this.directionsList = [];
        this.directionsStopList = [];
        let emptyData = { 'type': 'FeatureCollection', 'features': [] };
        this.getSource(this.DIRECTIONS_FEATURE).setData(emptyData as any as Feature<Geometry>);
        this.getSource(this.DIRECTIONS_LAYER).setData(emptyData as any as Feature<Geometry>);
    }

    /**
     * Fit map bounds to coords
     */
    public fitMapBoundsToCoords(coordinates: Position[], padding: PaddingOptions, speed: number = 0.8, autoZoom: boolean = false) {
        const bounds = this.calcMapboxBounds(coordinates);

        if(!padding) padding = { top: 30, left: 30, right: 30, bottom: 200 };
        let options: FitBoundsOptions = { padding, speed };
        // If zoom should not automatically adjust to bounds, set current zoom
        if(!autoZoom) options.zoom = this.map.getZoom();

        this.map.fitBounds(bounds, options);
    }

    /**
     * Reduce an array of mapbox positions to a single mapbox bound object
     */
    private calcMapboxBounds(positions: Position[]): mapboxgl.LngLatBounds {
        // Type cast GeoJson Positions into Mapbox LngLat - TODO: does this type mess work in all situations?
        let coordinates = positions as any as mapboxgl.LngLat[];
        let initBounds = new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]);
        // Reduce array of positions to single bound object
        return coordinates.reduce((bounds, coord) => {
            return bounds.extend(coord);
        }, initBounds);
    }

    /**
     * Check if given position is currently visible on map
     */
    public isPositionOnMap(pos: Coords): boolean {
        let coordinates = new mapboxgl.LngLat(pos.long, pos.lat);
        let currentBounds = this.map.getBounds();
        return currentBounds.contains(coordinates);
    }

    /**
     * Shows Route planner
     */
    public showDirections() {
        this.showLayer(this.DIRECTIONS_LAYER);
        this.DIRECTIONS_DOT_LAYERS.forEach((layer) => this.showLayer(layer));
        this.showLayer(this.DIRECTIONS_FEATURE);
        this.showLayer(this.DIRECTIONS_TEXT);
    }

    /**
     * Hides Route planner by setting layer geometry of layer 'routePlanner' to null
     */
    public hideDirections(): void {
        this.hideLayer(this.DIRECTIONS_LAYER);
        this.DIRECTIONS_DOT_LAYERS.forEach((layer) => this.hideLayer(layer));
        this.hideLayer(this.DIRECTIONS_FEATURE);
        this.hideLayer(this.DIRECTIONS_TEXT);
    }

    /**
    * Shows a search result on the map using 'searchResult' layer
    * Flies to location if given as parameter (default)
    */
    public showSearchResult(point: JourneyPoint, flyTo: boolean = true): void {
        const searchResultsLayer = 'searchResults';
        let json = point.asGeoJson();

        this.getSource(searchResultsLayer).setData(json);
        this.showLayer(searchResultsLayer);
        this.geocodeFeatureList = [json];

        if(!flyTo || !point) return
        this.flyTo(point.coords, 15);
    }

    /**
    * Hides a search result on the map using 'searchResult' layer
    */
    public hideSearchresult(): void {
        this.hideLayer('searchResults');
    }

    /**
    * Shows any layer on the map
    */
    public showLayer(layer: string, show: boolean = true): void {
        // Ignore layer if it does not exist in this map style
        if(!this.map?.getLayer(layer)) return;
        // Show or hide layer in map
        const visibility = (show) ? 'visible' : 'none';
        this.map.setLayoutProperty(layer, 'visibility', visibility);
    }

    /**
    * Hides any layer on the map
    */
    public hideLayer(layer: string): void {
        this.showLayer(layer, false);
    }

    /**
    * Shows motes layer on the map
    */
    public async showTour(): Promise<void> {
        await this.awaitInit();

        this.showLayer(this.MOTES_LAYER);
        this.showLayer(this.ROUTES_LAYER);
        this.showLayer(this.AVAILABLE_MOTE_LAYER);
        this.showLayer("clusters");
        this.showLayer("clusterCount");
    }

    /**
    * Hides motes layer on the map
    */
    public hideTour(): void {
        this.showLayer(this.MOTES_LAYER, false);
        this.showLayer(this.ROUTES_LAYER, false);
        this.showLayer(this.AVAILABLE_MOTE_LAYER, false);
        this.showLayer("clusters", false);
        this.showLayer("clusterCount", false);
    }

    /**
     * Get map snapshot media
     */
    public async getSnapshot(sources: string[]): Promise<Media> {
        // Wait for tiles
        await this.awaitTilesLoaded();

        // Wait for relevant sources
        const sourcePromises = sources.map((source) => this.awaitMapSource(source));
        await Promise.all(sourcePromises);

        // Wait for map to turn idle (route and motes have been drawn)
        await fromEvent(this.map, 'idle').pipe(take(1)).toPromise();

        let snapshot = new Media();
        snapshot.hd.url = this.map.getCanvas().toDataURL('image/webp');
        snapshot.hd.fallback = this.map.getCanvas().toDataURL('image/jpeg');
        snapshot.thumb.url = 'https://firebasestorage.googleapis.com/v0/b/motesdev.appspot.com/o/static-images%2Fbackgrounds_webp%2Fmap_thumb.webp?alt=media&token=bcf5f64c-d2ce-4800-9493-0c462d11391a';
        snapshot.thumb.fallback = 'https://firebasestorage.googleapis.com/v0/b/motesdev.appspot.com/o/static-images%2Fbackgrounds%2Fmap_thumb.jpg?alt=media&token=9a73c637-6e00-4a78-99c9-6776c2cbc0c2';
        snapshot.title = $localize`The tour route and stories shown on a map.`;
        snapshot.type = MediaType.IMAGE;

        return snapshot;
    }
}
